import { Component, computed, DestroyRef, effect, inject, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { IntelligenceModels } from '@models/intelligence.model';
import { VehicleGroupsModel } from '@models/vehicle-groups.model';
import { VehicleModels } from '@models/vehicle.model';
import { VehicleGroupsActions } from '@states/vehicle-groups/vehicle-groups.actions';
import { vehicleGroupsFeature } from '@states/vehicle-groups/vehicle-groups.feature';
import { UiButtonComponent, UiDatasetTableComponent, UiSelectedItemsSnackbarComponent } from '@ui';
import { IntelligenceResultsService } from '../../../../../services/intelligence-results.service';
import { VehicleGroupsDialogService } from '../../../../../services/vehicle-groups-dialog.service';
import { VehiclesTableComponent } from '../../../../../pages/database/pages/vehicles/components/vehicles-table/vehicles-table.component';
import { createVehicleGroupsTableConfig } from '../../../../../pages/database/pages/vehicles/pages/vehicle-groups/vehicle-groups-table.util';
import { api } from '@consts/url.const';
import { routerSegments } from '@consts/routes';

@Component({
  selector: 'app-vehicle-groups-result',
  standalone: true,
  imports: [
    CommonModule,
    MatTooltipModule,
    TranslatePipe,
    UiButtonComponent,
    UiDatasetTableComponent,
    UiSelectedItemsSnackbarComponent,
    VehiclesTableComponent,
  ],
  templateUrl: './vehicle-groups-result.component.html',
  styleUrls: ['./vehicle-groups-result.component.scss'],
})
export class VehicleGroupsResultComponent {
  private readonly httpClient = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly vehicleGroupsDialogService = inject(VehicleGroupsDialogService);
  readonly resultsService = inject(IntelligenceResultsService);

  protected readonly removeFromGroupTooltip = 'VEHICLE_GROUPS.DETAIL.REMOVE_FROM_GROUP';

  private readonly processingIds = this.store.selectSignal(
    vehicleGroupsFeature.selectVehicleGroupProcessingIds,
  );

  protected readonly membersTable = viewChild(VehiclesTableComponent);
  protected readonly selectedPlatesCount = computed(
    () => this.membersTable()?.selectedPlatesRows().size ?? 0,
  );

  /** Read directly from the service — self-updates without any parent binding. */
  readonly response = computed<
    | IntelligenceModels.VehicleGroups.Response
    | IntelligenceModels.VehicleGroups.MembersResponse
    | null
  >(() => {
    const result = this.resultsService.current();
    if (result?.type !== 'vehicle-groups') return null;
    return result.data as
      | IntelligenceModels.VehicleGroups.Response
      | IntelligenceModels.VehicleGroups.MembersResponse;
  });

  readonly groups = computed<VehicleGroupsModel.VehicleGroupListDocument[]>(() => {
    const data = this.response();
    return data && 'groups' in data ? data.groups : [];
  });

  readonly members = computed<IntelligenceModels.VehicleGroups.MembersResponse | null>(() => {
    const data = this.response();
    return data && 'vehicles' in data ? data : null;
  });

  readonly loadingGroupId = signal<string | null>(null);

  constructor() {
    // Publish this view's actions into the shared Cmd+K context bar; clear them
    // when this component unmounts (result type switches away from vehicle-groups).
    effect(
      () => {
        const membersData = this.members();
        this.resultsService.setHeaderActions(
          membersData
            ? [
                {
                  icon: 'ui-icon-plus',
                  tooltip: 'VEHICLE_GROUPS.DETAIL.ADD_VEHICLES',
                  onClick: () => this.addVehiclesToGroup(),
                },
                {
                  icon: 'ui-icon-new-tab',
                  tooltip: 'VEHICLE_GROUPS_RESULT.OPEN_GROUP_PAGE_TOOLTIP',
                  onClick: () =>
                    this.openInNewTab([routerSegments.vehicleGroups, membersData.groupId]),
                }
              ]
            : [],
        );
      },
      { allowSignalWrites: true },
    );
    this.destroyRef.onDestroy(() => this.resultsService.setHeaderActions([]));
  }

  protected readonly groupsTableConfig = computed(() =>
    createVehicleGroupsTableConfig({
      onRowClick: (group) => this.openGroup(group),
      processingIds: () => this.processingIds(),
    }),
  );

  async openGroup(group: VehicleGroupsModel.VehicleGroupListDocument): Promise<void> {
    if (this.loadingGroupId()) return;
    this.loadingGroupId.set(group._id);
    try {
      const response = await firstValueFrom(
        this.httpClient.get<IntelligenceModels.VehicleGroups.MembersResponse>(
          `${api.intelligence.vehicleGroups}/${group._id}/vehicles`,
        ),
      );
      const label = `${response.count} of ${response.totalCount} · ${response.groupName}`;
      // Use overlay so this drill-down does not insert a new history entry
      // and break the sequential message↔history mapping.
      this.resultsService.setOverlay(response, 'vehicle-groups', label);
    } finally {
      this.loadingGroupId.set(null);
    }
  }

  openGroupsPage(): void {
    this.openInNewTab([routerSegments.vehicleGroups]);
  }

  protected createGroup(): void {
    this.vehicleGroupsDialogService.openCreate();
  }

  protected onGroupRowAction(event: VehicleGroupsModel.DatasetRowActionEvent): void {
    switch (event.actionId) {
      case VehicleGroupsModel.RowActionId.Edit:
        this.vehicleGroupsDialogService.openEdit(event.row._id, event.row.name);
        break;
      case VehicleGroupsModel.RowActionId.Delete:
        this.store.dispatch(
          VehicleGroupsActions.requestDeleteVehicleGroup({ id: event.row._id, name: event.row.name }),
        );
        break;
    }
  }

  /**
   * Overrides the members table's built-in delete flow (same collision reason
   * as the plain vehicles result) with the production remove-from-group
   * effect, which owns its own confirm dialog.
   */
  protected removeMemberFromGroup = (vehicle: VehicleModels.Vehicle): void => {
    const membersData = this.members();
    if (!membersData || vehicle.id == null) return;
    const requestId = `${membersData.groupId}_${Date.now()}_${Math.random()}`;
    this.store.dispatch(
      VehicleGroupsActions.requestRemoveVehiclesFromGroup({
        groupId: membersData.groupId,
        groupName: membersData.groupName,
        vehicleIds: [vehicle.id],
        requestId,
      }),
    );
  };

  protected addVehiclesToGroup(): void {
    const membersData = this.members();
    if (!membersData) return;
    const memberIds = membersData.vehicles
      .map((v) => v.id)
      .filter((id): id is number => id != null);
    this.vehicleGroupsDialogService.openAddVehiclesToGroup(
      membersData.groupId,
      membersData.groupName,
      memberIds,
    );
  }

  protected selectAllDisplayedVehicles(): void {
    const table = this.membersTable();
    const membersData = this.members();
    if (!table || !membersData) {
      return;
    }
    const displayedPlates = membersData.vehicles
      .map((vehicle) => String(vehicle.plate ?? '').trim())
      .filter((plate) => plate.length > 0);
    const selected = table.selectedPlatesRows();
    const missingDisplayedPlates = displayedPlates.filter((plate) => !selected.has(plate));
    if (missingDisplayedPlates.length === 0) {
      return;
    }
    table.applyForcedSelection([...selected, ...missingDisplayedPlates]);
  }

  protected clearSelectedVehicles(): void {
    this.membersTable()?.applyForcedSelection([]);
  }

  protected removeSelectedMembers(): void {
    const table = this.membersTable();
    const membersData = this.members();
    if (!table || !membersData) {
      return;
    }
    const selectedPlates = table.selectedPlatesRows();
    const vehicleIds = membersData.vehicles
      .filter((vehicle) => selectedPlates.has(vehicle.plate))
      .map((vehicle) => vehicle.id)
      .filter((id): id is number => id != null);
    if (vehicleIds.length === 0) {
      return;
    }
    this.store.dispatch(
      VehicleGroupsActions.requestRemoveVehiclesFromGroup({
        groupId: membersData.groupId,
        groupName: membersData.groupName,
        vehicleIds,
        requestId: `${membersData.groupId}_${Date.now()}_${Math.random()}`,
      }),
    );
  }

  /** Open the target app page in a new tab so the current Cmd+K session stays open. */
  private openInNewTab(segments: string[]): void {
    const tree = this.router.createUrlTree(['/', routerSegments.database, ...segments]);
    const path = this.router.serializeUrl(tree);
    const url = window.location?.origin ? `${window.location.origin}${path}` : path;
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
